// @ts-strict-ignore
import {
  useCallback,
  useMemo,
  useState,
  useContext,
} from 'react';
import {
  Box,
  TextField,
  MultiSelect,
  Separator,
  HelpText,
} from '@usealloy/component-library';
import type { FieldErrors } from 'react-hook-form';
import {
  Controller, useFormContext, useFieldArray, useFormState,
} from 'react-hook-form';
import { UserContext } from '~/dashboard/context';
import { isInSnippetEditor } from 'flow/editor/components/helpers';
import type { FormState } from './types';
import {
  getServiceFailureDefaultOptions, convertActionOptionsToServiceOptions,
} from '../serviceOptionHelpers';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { setServiceActionOptionPublicIdInEditing, updateServiceActionValidation } from 'flow/store/redux/slice';
import {
  selectIsOnboardingWorkflow,
  selectFocusedAction,
  selectRecurringWorkflows,
  selectWorkflowFailureHandlingMode,
  selectServiceProp,
  selectGlobalServiceConfigOptions,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { ServiceConfigOption } from '~/dashboard/views/settings/service-configuration/ServiceConfigOptionView';
import { ServiceConfigModalMode } from '~/dashboard/views/settings/service-configuration/types';
import {
  ValidationTextField,
  RestoreLink,
} from '~/dashboard/views/settings/service-configuration/styles';
import { DISPLAY_NAME_EMPTY_ERROR } from '../constants';
import {
  StyledLocalSettingContainer,
} from './styles';
import { DISPLAY_NAME_OPTION_KEY, WEBHOOK_KEY } from 'flow/service/actionOptions';
import { FIRST_PARTY_SERVICES } from 'flow/service/services/constants';
import RecurringWebhookContainer from './RecurringWebhookContainer';
import type { NodeEditorAutoSaveTabProps } from '../../useNodeEditorForm/types';
import useNodeFormAutoSave from '../../useNodeFormAutoSave';
import { failureHandlingDescriptionBestEffortMode, failureHandlingDescriptionFullThresholdMode } from '../../constants';

export default function ServiceLocalSettingsTab({
  handleAutoSave,
  isReadOnly,
}: NodeEditorAutoSaveTabProps) {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const isOnboardingWorkflow = useAppSelector(selectIsOnboardingWorkflow);
  const recurringWorkflowsByService = useAppSelector(selectRecurringWorkflows);
  const failureHandlingModeValue = useAppSelector(selectWorkflowFailureHandlingMode);
  const { breadcrumbs } = useContext(UserContext);
  const isSnippetEditor = isInSnippetEditor(breadcrumbs);

  const service = useAppSelector(selectServiceProp(focusedAction.service.internalName));
  const globalServiceConfigOptions = useAppSelector(selectGlobalServiceConfigOptions);

  const {
    service_id: serviceId,
    serviceConfig,
    production_enabled: productionEnabled,
    sandbox_enabled: sandboxEnabled,
  } = service;

  const [isAddingOption, setIsAddingOption] = useState(false);

  const serviceFailureOptions = useMemo(() => getServiceFailureDefaultOptions(), []);
  const serviceOptions = useMemo(
    () => focusedAction && convertActionOptionsToServiceOptions(focusedAction.options),
    [focusedAction],
  );
  const allServiceConfigOptions = useMemo(
    () => [...serviceConfig.serviceConfigOptions, ...globalServiceConfigOptions],
    [serviceConfig, globalServiceConfigOptions],
  );

  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    handleSubmit,
    setError,
    clearErrors,
  } = useFormContext<FormState>();
  const { errors } = useFormState({ control });

  const {
    append: appendServiceConfigOption,
    fields: formattedServiceConfigOptions,
    remove: removeServiceConfigOption,
  } = useFieldArray({
    name: 'formattedServiceConfigOptions',
    control,
  });

  const watchFieldArray = watch('formattedServiceConfigOptions');
  const controlledFields = formattedServiceConfigOptions.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  const handleAutoSaveAndValidation = useCallback((formData: FormState) => {
    handleAutoSave(formData);
    dispatch(updateServiceActionValidation({
      publicId: focusedAction.publicId,
      data: [],
    }));
  }, [dispatch, focusedAction.publicId, handleAutoSave]);

  const handleAutoSaveValidationError = useCallback((formErrors: FieldErrors<FormState>) => {
    const displayNameOption = getValues('displayNameOption');
    const errorMessages = formErrors?.formattedServiceConfigOptions?.map(
      e => `${displayNameOption?.value}: ${e.value?.message}`,
    );
    dispatch(updateServiceActionValidation({
      publicId: focusedAction.publicId,
      data: errorMessages,
    }));
  }, [dispatch, focusedAction.publicId, getValues]);

  useNodeFormAutoSave({ fn: handleAutoSaveAndValidation });

  const submitAutoSave = useCallback(
    () => handleSubmit(handleAutoSaveAndValidation, handleAutoSaveValidationError)(),
    [handleAutoSaveAndValidation, handleAutoSaveValidationError, handleSubmit],
  );

  const serviceName = focusedAction.service.name;
  const focusedActionOptions = focusedAction.options;
  const isFirstPartyService = FIRST_PARTY_SERVICES.includes(focusedAction.service.internalName);

  const restoreDisplayNameToServiceName = useCallback(() => {
    clearErrors('displayNameOption.value');
    setValue('displayNameOption.value', serviceName, { shouldValidate: true, shouldDirty: true });
    submitAutoSave();
  }, [clearErrors, serviceName, setValue, submitAutoSave]);

  const commitDisplayNameField = useCallback((rawInput: string) => {
    const trimmed = rawInput.trim();
    if (trimmed.length === 0) {
      // if empty input - revert back to saved action option value or fall back to service name
      clearErrors('displayNameOption.value');
      const displayName = focusedActionOptions[DISPLAY_NAME_OPTION_KEY]?.value;
      const revert = displayName || serviceName;
      setValue('displayNameOption.value', String(revert), { shouldValidate: true, shouldDirty: true });
    } else if (rawInput !== trimmed) {
      // if trimmed - need to persist in form
      setValue('displayNameOption.value', trimmed, { shouldValidate: true, shouldDirty: true });
    }
    submitAutoSave();
  }, [clearErrors, focusedActionOptions, serviceName, setValue, submitAutoSave]);

  const serviceConfigOptionsMapping = allServiceConfigOptions.reduce(
    (acc, option) => {
      acc[option.serviceConfigOptionId] = option;
      return acc;
    }, {},
  );

  const handlingDescription = failureHandlingModeValue === 'full-threshold'
    ? failureHandlingDescriptionFullThresholdMode : failureHandlingDescriptionBestEffortMode;

  return (
    <StyledLocalSettingContainer>
      <Box gap={2} justifyContent="flex-start" alignItems="flex-start">
        <Box
          direction="column"
          gap={2}
          justifyContent="flex-start"
          style={{ padding: 'var(--cl-space-0) var(--cl-space-4)' }}
        >
          <Controller
            control={control}
            name="displayNameOption.value"
            rules={{
              validate: value => (
                (value != null && String(value).trim().length >= 1)
                || DISPLAY_NAME_EMPTY_ERROR
              ),
            }}
            render={({ field: { onChange, value, onBlur } }) => {
              const trimmedValue = String(value ?? '').trim();
              const trimmedServiceName = String(serviceName).trim();
              const showRestoreNameLink = !isReadOnly
                && trimmedValue !== trimmedServiceName;
              const displayNameFieldError = errors.displayNameOption?.value?.message;

              return (
                <Box
                  data-dd-action-name="service-local-settings-display-name"
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="stretch"
                  style={{ width: '100%' }}
                >
                  <TextField
                    label="Display Name"
                    type="text"
                    helpText={(
                      <Box justifyContent="flex-start" direction="row" style={{ width: '100%' }}>
                        <Box justifyContent="flex-start" style={{ flex: 1, minWidth: 0 }}>
                          <ValidationTextField>
                            Changes the name that is displayed for this service in the workflow node
                          </ValidationTextField>
                        </Box>
                        {showRestoreNameLink && (
                          <Box
                            justifyContent="flex-end"
                            style={{ width: '30%', flexShrink: 0 }}
                          >
                            <RestoreLink
                              onClick={e => {
                                e.preventDefault();
                                restoreDisplayNameToServiceName();
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  restoreDisplayNameToServiceName();
                                }
                              }}
                            >
                              restore name
                            </RestoreLink>
                          </Box>
                        )}
                      </Box>
                    )}
                    error={displayNameFieldError || false}
                    value={value}
                    onChange={e => {
                      onChange(e);
                      const trimmed = String(e.target.value ?? '').trim();
                      if (!trimmed) {
                        setError(
                          'displayNameOption.value',
                          { type: 'manual', message: DISPLAY_NAME_EMPTY_ERROR },
                          { shouldFocus: false },
                        );
                      } else {
                        clearErrors('displayNameOption.value');
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    onBlur={e => {
                      onBlur();
                      commitDisplayNameField(e.target.value ?? '');
                    }}
                    disabled={isReadOnly}
                  />
                </Box>
              );
            }}
          />

          {isOnboardingWorkflow
            && recurringWorkflowsByService[focusedAction.service.name]
            && (
              <Controller
                name="recurringWorkflow"
                control={control}
                render={({ field: { onChange } }) => (
                  <RecurringWebhookContainer
                    initialSelectedRecurringWorkflow={serviceOptions.find(
                      option => option.key === WEBHOOK_KEY)}
                    recurringWorkflowOptions={
                      recurringWorkflowsByService[focusedAction.service.name]
                    }
                    onChange={e => {
                      onChange(e);
                      submitAutoSave();
                    }}
                    readOnly={isReadOnly}
                  />
                )}
              />
            )}
        </Box>
        <Box
          direction="column"
          gap={2}
          style={{ padding: 'var(--cl-space-0) var(--cl-space-4) var(--cl-space-1)' }}
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Controller
            name="serviceFailureDefaultOption"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MultiSelect
                isMulti={false}
                label="Service Failure Handling - if data is missing:"
                helpText={handlingDescription}
                onChange={e => {
                  onChange(e);
                  submitAutoSave();
                }}
                options={serviceFailureOptions}
                selectedOptions={value}
                isDisabled={isReadOnly}
              />
            )}
          />
          <HelpText>
            Controls what action to take if the Service fails to run
          </HelpText>
        </Box>
      </Box>
      {!isFirstPartyService && <Separator />}
      {!isFirstPartyService && serviceConfig && (
        <ServiceConfigOption
          mode={ServiceConfigModalMode.NODE}
          serviceConfigOptionsMapping={serviceConfigOptionsMapping}
          formattedServiceConfigOptions={controlledFields}
          watch={watch}
          errors={errors}
          control={control}
          setValue={setValue}
          trigger={trigger}
          appendServiceConfigOption={appendServiceConfigOption}
          removeServiceConfigOption={removeServiceConfigOption}
          selectedAlloyOptions={serviceConfig.selectedAlloyOptions}
          selectedCustomerOptions={serviceConfig.selectedCustomerOptions}
          isAddingOption={isAddingOption}
          setIsAddingOption={
            isAdding => {
              setIsAddingOption(isAdding);
              if (isAdding) {
                dispatch(setServiceActionOptionPublicIdInEditing('true'));
              } else {
                dispatch(setServiceActionOptionPublicIdInEditing(null));
              }
            }
          }
          serviceName={service.name}
          serviceId={serviceId}
          submitAutoSave={submitAutoSave}
          onlySandboxEnabled={!productionEnabled && sandboxEnabled}
          isSnippetEditor={isSnippetEditor}
        />
      )}
    </StyledLocalSettingContainer>
  );
}
